import { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface ClearBufferConfirmProps {
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  bufferSize: number;
}

/**
 * ClearBufferConfirm — Buffer clear confirmation dialog
 * role=dialog · aria-modal=true · focus trap · ESC cancels
 */
export function ClearBufferConfirm({
  onConfirm,
  onCancel,
  isLoading,
  bufferSize,
}: ClearBufferConfirmProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // ESC cancels
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  // Focus cancel button on open (cancel is the safe default action)
  useEffect(() => {
    setTimeout(() => cancelRef.current?.focus(), 50);
  }, []);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  return (
    <>
      {/* Backdrop overlay */}
      <div
        aria-hidden="true"
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--color-overlay-backdrop)',
          zIndex: 300,
        }}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="em-clear-dialog-title"
        aria-describedby="em-clear-dialog-desc"
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(480px, 90vw)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 301,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--spacing-4) var(--spacing-6)',
            background: 'var(--color-surface-elevated)',
            borderBottom: '1px solid var(--color-surface-border)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-warning)',
            }}
            aria-hidden="true"
          >
            ⚠️
          </span>
          <h2
            id="em-clear-dialog-title"
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            {t('eventMonitor.dialog.clearTitle')}
          </h2>
        </div>

        {/* Body */}
        <div
          id="em-clear-dialog-desc"
          style={{
            padding: 'var(--spacing-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-primary)',
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            {t('eventMonitor.dialog.clearWarn')}{' '}
            <strong style={{ color: 'var(--color-danger)' }}>
              {t('eventMonitor.dialog.clearAuditNote')}
            </strong>
          </p>

          <div
            style={{
              background: 'color-mix(in srgb, var(--color-warning) 10%, var(--color-surface-muted))',
              border: '1px solid var(--color-warning)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--spacing-3) var(--spacing-4)',
              display: 'flex',
              gap: 'var(--spacing-3)',
              alignItems: 'center',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 'var(--font-size-xl)' }}>
              🗑️
            </span>
            <span>
              {t('eventMonitor.dialog.clearCount')}{' '}
              <span
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontWeight: 700,
                  color: 'var(--color-warning)',
                }}
              >
                {bufferSize}
              </span>
              {' '}{t('eventMonitor.dialog.clearCountSuffix')}
            </span>
          </div>

          <p
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              margin: 0,
            }}
          >
            {t('eventMonitor.dialog.clearAuditLog')}
          </p>
        </div>

        {/* Footer buttons */}
        <div
          style={{
            padding: 'var(--spacing-4) var(--spacing-6)',
            borderTop: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-elevated)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-3)',
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            aria-label={t('eventMonitor.dialog.ariaCancel')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('eventMonitor.dialog.clearCancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            aria-label={t('eventMonitor.dialog.ariaConfirm')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-5)',
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              fontFamily: 'var(--font-family-sans)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isLoading ? (
              <>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    display: 'inline-block',
                    animation: 'em-rotate 1s linear infinite',
                  }}
                />
                {t('eventMonitor.dialog.clearProcessing')}
              </>
            ) : (
              t('eventMonitor.dialog.clearConfirm')
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes em-rotate { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
